import _ from 'lodash';
import { createTranslator } from 'short-uuid';
import profaneWords from 'profane-words';

const uuidTranslator = createTranslator();

export function inBrowser() {
  return typeof window !== 'undefined';
}


export const jlog = obj => {
  if (typeof window !== 'undefined') {
    // eslint-disable-next-line no-console
    console.table(obj);
  } else {
    // eslint-disable-next-line no-console
    console.log(JSON.stringify(obj, null, 2));
  }
};

export const DEVMODE = process.env.NODE_ENV === 'development';

// K_SERVICE is set automatically by Cloud Run to the service name. Use this
// to fork behavior that's specific to Cloud Run vs. our GCE VM deploys
// (e.g., skipping GCE metadata fetches that would otherwise 404).
export const isCloudRun = () => Boolean(process.env.K_SERVICE);

export const SECONDS = 1000;
export const MINUTES = 60 * SECONDS;
export const HOURS = 60 * MINUTES;
export const DAYS = 24 * HOURS;

export const partialIp = ip => {
  let parts = _.split(ip, '.');
  // ip4
  if (_.size(parts) > 2) {
    const partial = _.join(_.dropRight(parts, _.size(parts) - 2), '.');
    return partial;
  }

  // ip 6
  parts = _.split(ip, ':');
  if (_.size(parts) === 8) {
    const partial = _.join(_.dropRight(parts, _.size(parts) - 4), ':');
    return partial;
  }

  return ip;
};

export const BETA_HOSTS = [
  'beta.raidbots.com',
  'ulduar.raidbots.com',
  'karazhan.raidbots.com',
  'moltencore.raidbots.com',
  'antorus.raidbots.com',
  'mimiron.raidbots.com',
];

export const isBetaHost = host => {
  return _.includes(BETA_HOSTS, host) || process.env.RAIDBOTS_ENV === 'beta';
};

// RFC 5321 caps the whole address at 254 chars.
export const MAX_EMAIL_LENGTH = 254;

// The WHATWG/HTML5 `<input type="email">` pattern, with the final domain group
// pinned to `+` (was `*`) so at least one dot is required — i.e. a TLD-less
// `user@localhost` is rejected. Single source of truth for both the server-side
// throwOnInvalidEmail and the frontend pre-submit checks; keep the two in sync
// by importing this rather than re-deriving a regex.
// eslint-disable-next-line max-len
const EMAIL_REGEX = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;

// Validates the string exactly as given — no trimming. Callers that accept raw
// user input (signup form, admin edit) must trim first; the server normalize
// path already does.
export const isValidEmail = email => {
  if (!_.isString(email) || email.length === 0 || email.length > MAX_EMAIL_LENGTH) {
    return false;
  }
  return EMAIL_REGEX.test(email);
};

export const shouldRunExperiment = (rawPercent) => {
  const percent = parseInt(rawPercent, 10) || 0;
  let experimentRolled = false;
  if (percent > 0) {
    const roll = _.random(0, 99);
    experimentRolled = roll >= (100 - percent);
  }
  return experimentRolled;
};

// loose, lodash-flavored take on SimC's util::tokenize — snakeCase splits on
// apostrophes (zul'jin's -> zul_jin_s) and collapses separator runs to one
// underscore, where simc deletes apostrophes (zuljins) and emits one _ per
// space (chest - mark -> chest__mark). Baked into BuffReport matching and
// data-tools' generated tokenizedName fields; for parsing simc-produced
// tokens use simcTokenize in shared/string.js, which is byte-faithful.
export const tokenize = str => {
  let result = str;

  result = _.trimStart(result, '_+');
  result = result.replace(/[-+.%]+/g, '');
  result = _.toLower(result);
  result = _.snakeCase(result);

  return result;
};

export function deepFreeze(object) {
  // Retrieve the property names defined on object
  const propNames = Object.getOwnPropertyNames(object);

  // Freeze properties before freezing self

  // eslint-disable-next-line no-restricted-syntax
  for (const name of propNames) {
    const value = object[name];

    if (value && typeof value === 'object') {
      deepFreeze(value);
    }
  }

  return Object.freeze(object);
}

// Generate an internal GUID (for item deduplication, cache keys, etc.)
// crypto is a global in both Node.js and browsers.
export function generateGuid() {
  return crypto.randomUUID();
}

// Generate a URL safe UUID that tries to exclude profanity
export function generateUrlSafeUuid(strict = false, maxAttempts = 10) {
  let uuid;
  let containsProfanity = false;
  let attempts = 0;

  do {
    uuid = uuidTranslator.generate();
    const uuidLower = uuid.toLowerCase();
    containsProfanity = _.some(profaneWords, word => uuidLower.includes(word));
    attempts += 1;
  } while (containsProfanity && attempts < maxAttempts);

  if (containsProfanity && strict) {
    throw new Error('uuid-profane');
  }

  return uuid;
}

// Sim ids are generated above, so validate against that shape: url-safe
// characters only, bounded length. Callers use this to keep junk out of redis
// keys/channel names and datastore lookups built from a raw request param.
const SIM_ID_RE = /^[A-Za-z0-9_-]{8,64}$/;

export function isValidSimId(simId) {
  return typeof simId === 'string' && SIM_ID_RE.test(simId);
}

// Retry an async thunk with exponential backoff. Resolves with fn()'s value on
// the first success; if every attempt throws, rejects with the last error. fn
// receives the 1-based attempt number. onRetry(error, attempt) (optional) fires
// after each failed attempt that will be retried (not after the final one).
// baseDelayMs: 0 skips the sleeps (tests pass it to run fast).
export const retryAsync = async (
  fn,
  {
    attempts = 4, baseDelayMs = 250, factor = 2, onRetry,
  } = {},
) => {
  let lastError;
  for (let attempt = 1; attempt <= attempts; attempt += 1) {
    try {
      // eslint-disable-next-line no-await-in-loop
      return await fn(attempt);
    } catch (error) {
      lastError = error;
      if (attempt >= attempts) break;
      if (onRetry) onRetry(error, attempt);
      const ms = baseDelayMs * (factor ** (attempt - 1));
      if (ms > 0) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => { setTimeout(resolve, ms); });
      }
    }
  }
  throw lastError;
};
